import { client, fetchEvent } from "../http/events";
import { OWNER_HEADER } from "./workspace";

// The Teams fields of the GraphQL API
// (eventlive-cdk/appsync/teams-youthstream/schema-additions.graphql, CONTRACT.md).
// Errors are left to the caller: teamsErrorMessage() turns them into text.

const memberFields = `
  id
  owner
  slug
  role
  status
  email
  name
  invited_by
  created_at
  accepted_at
  expires_at
`;

const sharedFields = `
  owner
  owner_name
  company_name
  logo_path
  slug
  event_title
  role
  app
`;

// The owner's profile as a member sees it: what event screens need (plan,
// account type, custom domain, branding) and the credit balance, which the
// server shows to the account's admins only (null for everyone else). Email,
// Stripe and ad ids stay hidden from members, so they aren't asked for.
export const workspaceProfileQuery = `
  query WorkspaceProfile {
    broadcasterProfile {
      username
      first_name
      last_name
      company_name
      company_url
      logo_path
      no_branding
      account_type
      custom_domain
      broadcaster_role
      legacy_pricing
      registered_datetime
      subscription_status
      subscription_plan
      subscription_access_expiry_datetime
      credits
    }
  }
`;

const run = async (query, variables, headers) => {
  const { data } = await client.graphql({ query, variables }, headers);
  return data;
};

// Personal: the accounts and events shared with the signed-in user.
export async function fetchSharedWithMe() {
  const data = await run(
    `query SharedWithMe { sharedWithMe { ${sharedFields} } }`
  );
  return (data?.sharedWithMe || []).filter(Boolean);
}

// The owner's profile, asked for in their account (x-el-owner). Its username
// says whether the server let us in: without Teams switched on for that
// account it answers with your own profile instead.
export async function fetchWorkspaceProfile(owner) {
  const data = await run(workspaceProfileQuery, undefined, {
    [OWNER_HEADER]: owner,
  });
  return data?.broadcasterProfile || null;
}

// The events of an account where only some events are shared with you: the
// server refuses that account's event lists to event members, so each shared
// event is loaded on its own, with its rooms (a share covers them).
export async function fetchSharedEvents(slugs) {
  const errors = [];
  const load = async (slug) => {
    try {
      const data = await fetchEvent(slug);
      return data?.myEvent || null;
    } catch (err) {
      errors.push(err);
      return null;
    }
  };
  const wanted = [...new Set((slugs || []).filter(Boolean))];
  const primaries = (await Promise.all(wanted.map(load))).filter(Boolean);
  if (!primaries.length && errors.length) throw errors[0];
  const roomSlugs = [
    ...new Set(
      primaries.flatMap((event) =>
        Array.isArray(event.room_slugs) ? event.room_slugs : []
      )
    ),
  ].filter((slug) => !wanted.includes(slug));
  const rooms = (await Promise.all(roomSlugs.map(load))).filter(Boolean);
  return [...primaries, ...rooms];
}

// The current workspace's people: the account's members and invites, plus the
// event's when `slug` is given.
export async function fetchTeamMembers(slug = null) {
  const data = await run(
    `query TeamMembers($slug: String) { teamMembers(slug: $slug) { ${memberFields} } }`,
    { slug: slug || null }
  );
  return (data?.teamMembers || []).filter(Boolean);
}

export async function inviteTeamMember({ email, role, slug = null }) {
  const data = await run(
    `mutation InviteTeamMember($email: String!, $role: TeamRole!, $slug: String) {
      inviteTeamMember(email: $email, role: $role, slug: $slug) { ${memberFields} }
    }`,
    { email, role, slug: slug || null }
  );
  return data?.inviteTeamMember || null;
}

export async function updateTeamMember(id, role) {
  const data = await run(
    `mutation UpdateTeamMember($id: ID!, $role: TeamRole!) {
      updateTeamMember(id: $id, role: $role) { ${memberFields} }
    }`,
    { id, role }
  );
  return data?.updateTeamMember || null;
}

// resetStreamKeys: the upcoming events they could open get new stream keys
// (events that streamed or have video keep theirs).
export async function removeTeamMember(id, { resetStreamKeys = false } = {}) {
  const data = await run(
    resetStreamKeys
      ? `mutation RemoveTeamMember($id: ID!) { removeTeamMember(id: $id, resetStreamKeys: true) }`
      : `mutation RemoveTeamMember($id: ID!) { removeTeamMember(id: $id) }`,
    { id }
  );
  return !!data?.removeTeamMember;
}

export async function resendTeamInvite(id) {
  const data = await run(
    `mutation ResendTeamInvite($id: ID!) { resendTeamInvite(id: $id) { ${memberFields} } }`,
    { id }
  );
  return data?.resendTeamInvite || null;
}

// Personal: joins the account or event behind an invite link.
export async function acceptTeamInvite(token) {
  const data = await run(
    `mutation AcceptTeamInvite($token: String!) { acceptTeamInvite(token: $token) { ${sharedFields} } }`,
    { token }
  );
  return data?.acceptTeamInvite || null;
}

// Personal: stop working in someone's account (or on one of their events).
export async function leaveTeam(owner, slug = null) {
  const data = await run(
    `mutation LeaveTeam($owner: String!, $slug: String) { leaveTeam(owner: $owner, slug: $slug) }`,
    { owner, slug: slug || null }
  );
  return !!data?.leaveTeam;
}

// What the invite page may show before anyone signs in. The schema allows the
// public API key or IAM; the app has no API key, so it goes as IAM, which is
// the identity pool's guest identity when signed out.
export async function fetchTeamInvite(token) {
  const data = await run(
    `query TeamInvite($token: String!) {
      teamInvite(token: $token) {
        inviter_name
        account_name
        event_title
        role
        email_hint
        status
        app
      }
    }`,
    { token }
  );
  return data?.teamInvite || null;
}
